import { defineStore } from 'pinia'
import { DEFAULT_QUESTION_ORDER } from '@/config/questions'
import { DEBT_AMOUNT_VALUES } from '@/config/scoring'

export const useFormStore = defineStore('form', {
  state: () => ({
    // Pain signal question values
    values: {
      painSignals: [],      // Array of selected pain signals
      worryFrequency: null, // How often they think about debt
      barrier: null,        // Main barrier to paying off debt
      debtAmount: null      // Debt amount bucket
    },

    // Pain scoring
    painScore: 0,
    painTier: 'cold',

    // Name fields
    name: {
      first: '',
      last: ''
    },

    // Email fields
    email: {
      value: '',
      sendtoAC: true
    },

    // Phone fields
    phone: {
      value: '',
      unmasked: '',
      completed: false,
      tcpaLanguage: '',
      validationStatus: 'pending',
      overrideValidation: false
    },

    // Address fields
    address: {
      address: '',
      zip: '',
      city: '',
      state: '',
      overrideValidation: false
    },

    // DOB fields
    dob: {
      day: '',
      month: '',
      year: ''
    },

    // Navigation state
    questionOrder: [...DEFAULT_QUESTION_ORDER],
    currentProgress: 0,
    transitionDirection: 'forwards',
    removeQuestion: false,

    // UI state
    backButtonVisible: true,
    trackingDisclosureVisible: false,
    showSpinner: false,
    displayThankYou: false
  }),

  getters: {
    // Current question ID
    currentQuestionId: (state) => {
      return state.questionOrder[state.currentProgress]
    },

    // Progress percentage
    progressPercent: (state) => {
      return Math.round(((state.currentProgress + 1) / (state.questionOrder.length + 1)) * 100)
    },

    // Get verified debt value from debt amount selection
    verifiedDebt: (state) => {
      return DEBT_AMOUNT_VALUES[state.values.debtAmount] || 0
    },

    // Validation getters
    isPainSignalsValid: (state) => state.values.painSignals.length > 0,

    isWorryFrequencyValid: (state) => state.values.worryFrequency !== null,

    isBarrierValid: (state) => state.values.barrier !== null,

    isDebtAmountValid: (state) => state.values.debtAmount !== null,

    isNameValid: (state) => {
      return !!(state.name.first && state.name.last)
    },

    isEmailValid: (state) => {
      return !!state.email.value
    },

    isPhoneValid: (state) => {
      if (state.phone.overrideValidation) return false
      return !!state.phone.value
    },

    isAddressValid: (state) => {
      if (state.address.overrideValidation) return false
      return !!(state.address.address && state.address.zip)
    },

    isDobValid: (state) => {
      return !!(state.dob.day && state.dob.month && state.dob.year)
    },

    // Check if location data is populated
    hasLocationData: (state) => {
      return !!(state.address.zip && state.address.city && state.address.state)
    },

    // Get name chat message (based on pain score)
    nameChatMessage: (state) => {
      if (state.painScore >= 60) {
        return '<p>Based on your answers, debt relief could significantly help your situation.</p>'
      } else if (state.painScore >= 40) {
        return '<p>Let\'s see what debt relief options are available for you.</p>'
      }
      return ''
    },

    // Build complete lead data object for submission
    leadData: (state) => {
      return {
        // Pain signal data
        painSignals: state.values.painSignals,
        worryFrequency: state.values.worryFrequency,
        barrier: state.values.barrier,
        debtAmount: state.values.debtAmount,
        painScore: state.painScore,
        painTier: state.painTier,
        // Verified debt from selection
        verifiedDebt: DEBT_AMOUNT_VALUES[state.values.debtAmount] || 0,
        // PII
        firstName: state.name.first,
        lastName: state.name.last,
        email: state.email.value,
        phone: state.phone.unmasked,
        address: state.address.address,
        city: state.address.city,
        state: state.address.state,
        zip: state.address.zip,
        dobDay: state.dob.day,
        dobMonth: state.dob.month,
        dobYear: state.dob.year,
        tcpaLanguage: state.phone.tcpaLanguage,
        validationStatus: state.phone.validationStatus
      }
    }
  },

  actions: {
    // Safe JSON parse with fallback to raw value (backwards compatibility)
    safeJsonParse(value, fallback = '') {
      if (!value) return fallback
      try {
        return JSON.parse(value)
      } catch {
        return value // Return raw value if not valid JSON
      }
    },

    // Load all values from localStorage
    loadFromStorage() {
      const params = new URLSearchParams(window.location.search)

      // Load pain signal values
      const storedPainSignals = localStorage.getItem('painSignals')
      if (storedPainSignals) {
        this.values.painSignals = this.safeJsonParse(storedPainSignals, [])
      }

      const storedWorryFrequency = localStorage.getItem('worryFrequency')
      if (storedWorryFrequency) {
        this.values.worryFrequency = this.safeJsonParse(storedWorryFrequency)
      }

      const storedBarrier = localStorage.getItem('barrier')
      if (storedBarrier) {
        this.values.barrier = this.safeJsonParse(storedBarrier)
      }

      const storedDebtAmount = localStorage.getItem('debtAmount')
      if (storedDebtAmount) {
        this.values.debtAmount = this.safeJsonParse(storedDebtAmount)
      }

      // Load pain scoring
      const storedPainScore = localStorage.getItem('painScore')
      if (storedPainScore) {
        this.painScore = this.safeJsonParse(storedPainScore, 0)
      }

      const storedPainTier = localStorage.getItem('painTier')
      if (storedPainTier) {
        this.painTier = this.safeJsonParse(storedPainTier, 'cold')
      }

      // Load name (with URL param override)
      const fnameParam = params.get('fname')
      const lnameParam = params.get('lname')
      this.name.first = (fnameParam && fnameParam !== '{fname}')
        ? fnameParam
        : this.safeJsonParse(localStorage.getItem('firstname'), '')
      this.name.last = (lnameParam && lnameParam !== '{lname}')
        ? lnameParam
        : this.safeJsonParse(localStorage.getItem('lastname'), '')

      // Load email (with URL param override)
      const emailParam = params.get('email')
      this.email.value = (emailParam && emailParam !== '{email}')
        ? emailParam
        : this.safeJsonParse(localStorage.getItem('email'), '')

      // Load phone (with URL param override)
      const phoneParam = this.cleanPhoneParam(params.get('phone'))
      this.phone.value = phoneParam || this.safeJsonParse(localStorage.getItem('phone'), '')
      if (phoneParam) {
        this.phone.unmasked = phoneParam.replace(/\D/g, '')
        this.phone.completed = this.phone.unmasked.length === 10
      }

      // Load address
      this.address.address = this.safeJsonParse(localStorage.getItem('address'), '')
      this.address.zip = this.safeJsonParse(localStorage.getItem('zip'), '')
      this.address.city = this.safeJsonParse(localStorage.getItem('city'), '')
      this.address.state = this.safeJsonParse(localStorage.getItem('state'), '')

      // Load DOB
      this.dob.day = this.safeJsonParse(localStorage.getItem('day'), '')
      this.dob.month = this.safeJsonParse(localStorage.getItem('month'), '')
      this.dob.year = this.safeJsonParse(localStorage.getItem('year'), '')

      // Load question order
      const storedOrder = localStorage.getItem('questionOrder')
      if (storedOrder) {
        this.questionOrder = this.safeJsonParse(storedOrder, [])
      }
    },

    cleanPhoneParam(phone) {
      if (!phone) return null
      let cleaned = phone.replace(/-/g, '')
      if (cleaned.startsWith('1') || cleaned.startsWith('+1') || cleaned.startsWith(' 1')) {
        cleaned = cleaned.slice(-10)
      }
      return cleaned.length === 10 ? cleaned : null
    },

    // Set question order
    setQuestionOrder(order) {
      this.questionOrder = order
      localStorage.setItem('questionOrder', JSON.stringify(order))
    },

    // Set pain signals (array)
    setPainSignals(signals) {
      this.values.painSignals = signals
      localStorage.setItem('painSignals', JSON.stringify(signals))
    },

    // Set a simple value
    setValue(key, value) {
      if (this.values.hasOwnProperty(key)) {
        this.values[key] = value
        this.saveToStorage(key, value)
      }
    },

    // Set pain score and tier
    setPainScore(score, tier) {
      this.painScore = score
      this.painTier = tier
      localStorage.setItem('painScore', JSON.stringify(score))
      localStorage.setItem('painTier', JSON.stringify(tier))
    },

    // Set name
    setName(first, last) {
      this.name.first = first
      this.name.last = last
      localStorage.setItem('firstname', JSON.stringify(first))
      localStorage.setItem('lastname', JSON.stringify(last))
    },

    // Set email
    setEmail(value, sendtoAC = true) {
      this.email.value = value
      this.email.sendtoAC = sendtoAC
      localStorage.setItem('email', JSON.stringify(value))
    },

    // Set phone
    setPhone(value, tcpaLanguage = '', validationStatus = 'verified') {
      this.phone.value = value
      this.phone.unmasked = value ? value.replace(/\D/g, '') : ''
      this.phone.completed = this.phone.unmasked.length === 10
      this.phone.tcpaLanguage = tcpaLanguage
      this.phone.validationStatus = validationStatus
      localStorage.setItem('phone', JSON.stringify(value))
    },

    // Set phone TCPA language
    setPhoneTcpa(language) {
      this.phone.tcpaLanguage = language
    },

    // Set phone validation status
    setPhoneValidationStatus(status) {
      this.phone.validationStatus = status
    },

    // Set phone override validation
    setPhoneOverrideValidation(override) {
      this.phone.overrideValidation = override
    },

    // Set address
    setAddress(address, zip, city, state) {
      if (address !== undefined) this.address.address = address
      if (zip !== undefined) this.address.zip = zip
      if (city !== undefined) this.address.city = city
      if (state !== undefined) this.address.state = state

      if (address !== undefined) localStorage.setItem('address', JSON.stringify(address))
      if (zip !== undefined) localStorage.setItem('zip', JSON.stringify(zip))
      if (city !== undefined) localStorage.setItem('city', JSON.stringify(city))
      if (state !== undefined) localStorage.setItem('state', JSON.stringify(state))
    },

    // Set address override validation
    setAddressOverrideValidation(override) {
      this.address.overrideValidation = override
    },

    // Set DOB
    setDob(month, day, year) {
      this.dob.month = month
      this.dob.day = day
      this.dob.year = year
      localStorage.setItem('month', JSON.stringify(month))
      localStorage.setItem('day', JSON.stringify(day))
      localStorage.setItem('year', JSON.stringify(year))
    },

    // Save a value to localStorage
    saveToStorage(key, value) {
      localStorage.setItem(key, JSON.stringify(value))
    },

    // Navigation: proceed to next question
    proceed() {
      this.transitionDirection = 'forwards'
      this.removeQuestion = true

      setTimeout(() => {
        this.currentProgress++
        this.removeQuestion = false

        // Scroll to top
        document.getElementById('header')?.scrollIntoView()

        // Update URL hash
        const currentId = this.questionOrder[this.currentProgress]
        history.pushState({ currentProgress: this.currentProgress }, '', '#' + currentId)

        // Save question order
        localStorage.setItem('questionOrder', JSON.stringify(this.questionOrder))
      }, 10)
    },

    // Navigation: go back
    goBack() {
      history.back()
    },

    // Handle browser back/forward
    handlePopState(event) {
      if (event.state === null) {
        window.history.go(-1)
      } else {
        this.transitionDirection = this.currentProgress < event.state.currentProgress ? 'forwards' : 'backwards'
        this.removeQuestion = true

        setTimeout(() => {
          this.currentProgress = event.state.currentProgress
          this.removeQuestion = false
          document.getElementById('header')?.scrollIntoView()
          localStorage.setItem('questionOrder', JSON.stringify(this.questionOrder))
        }, 10)
      }
    },

    // Set current progress (used when restoring from URL hash)
    setCurrentProgress(progress) {
      this.currentProgress = progress
    },

    // UI: hide back button
    hideBackButton() {
      this.backButtonVisible = false
    },

    // UI: show back button
    showBackButton() {
      this.backButtonVisible = true
    },

    // UI: show tracking disclosure
    showTrackingDisclosure() {
      this.trackingDisclosureVisible = true
    },

    // UI: hide tracking disclosure
    hideTrackingDisclosure() {
      this.trackingDisclosureVisible = false
    },

    // UI: show spinner
    showSpinnerUI() {
      this.showSpinner = true
    },

    // UI: show thank you
    showThankYou() {
      this.displayThankYou = true
    },

    // Check if a specific question is valid
    isQuestionValid(questionId) {
      switch (questionId) {
        case 'painSignals':
          return this.values.painSignals.length > 0
        case 'worryFrequency':
          return this.values.worryFrequency !== null
        case 'barrier':
          return this.values.barrier !== null
        case 'debtAmount':
          return this.values.debtAmount !== null
        case 'name':
          return !!(this.name.first && this.name.last)
        case 'email':
          return !!this.email.value
        case 'phone':
          return !!this.phone.value
        case 'address':
          return !!(this.address.address && this.address.zip)
        case 'dob':
          return !!(this.dob.day && this.dob.month && this.dob.year)
        default:
          return this.values[questionId] !== null && this.values[questionId] !== undefined
      }
    },

    // Check if all questions up to a point have valid data
    validateProgressTo(targetProgress) {
      for (let i = 0; i < targetProgress; i++) {
        const questionId = this.questionOrder[i]

        if (!this.isQuestionValid(questionId)) {
          return i
        }
      }

      return targetProgress
    }
  }
})
